import React, { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useUIModalStore } from '../../stores/uiModalStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useLanguage } from '../../hooks/useLanguage';
import type { Lang } from '../../hooks/useLanguage';
import { trackEvent } from '../../lib/analytics';
import { needsBillingRecovery } from '../../lib/billingRecovery';
import './HeaderAuthButton.css';

const t = (lang: Lang) => ({
  login: { ja: 'ログイン', en: 'Sign In', ko: '로그인', zh: '登录', es: 'Iniciar sesión', fr: 'Connexion', de: 'Anmelden', pt: 'Entrar' }[lang],
  logout: { ja: 'ログアウト', en: 'Sign Out', ko: '로그아웃', zh: '退出', es: 'Cerrar sesión', fr: 'Déconnexion', de: 'Abmelden', pt: 'Sair' }[lang],
  manageBilling: { ja: '請求とプランの管理', en: 'Manage billing & plan', ko: '결제 및 요금제 관리', zh: '管理订阅与账单', es: 'Facturación y plan', fr: 'Facturation et abonnement', de: 'Abrechnung & Tarif', pt: 'Faturamento e plano' }[lang],
  upgrade: { ja: 'Proにアップグレード', en: 'Upgrade to Pro', ko: 'Pro로 업그레이드', zh: '升级到 Pro', es: 'Mejorar a Pro', fr: 'Passer à Pro', de: 'Auf Pro upgraden', pt: 'Mudar para o Pro' }[lang],
  renewsOn: { ja: '次回更新', en: 'Renews', ko: '갱신일', zh: '续订日期', es: 'Se renueva', fr: 'Renouvellement', de: 'Verlängert am', pt: 'Renova em' }[lang],
  endsOn: { ja: '終了日', en: 'Ends', ko: '종료일', zh: '到期日', es: 'Termina', fr: 'Fin', de: 'Endet am', pt: 'Termina em' }[lang],
  portalError: { ja: '請求ページを開けませんでした。時間をおいて再度お試しください。', en: "Couldn't open the billing page. Please try again.", ko: '결제 페이지를 열 수 없습니다. 잠시 후 다시 시도해 주세요.', zh: '无法打开账单页面，请稍后重试。', es: 'No se pudo abrir la página de facturación. Inténtalo de nuevo.', fr: "Impossible d'ouvrir la page de facturation. Réessayez.", de: 'Abrechnungsseite konnte nicht geöffnet werden. Bitte erneut versuchen.', pt: 'Não foi possível abrir a página de faturamento. Tente novamente.' }[lang],
  goPro: { ja: 'Pro', en: 'Go Pro', ko: 'Pro 시작', zh: '升级 Pro', es: 'Hazte Pro', fr: 'Passer Pro', de: 'Pro holen', pt: 'Assinar Pro' }[lang],
  reviewPayment: { ja: 'お支払いを確認', en: 'Review payment', ko: '결제 확인', zh: '查看付款', es: 'Revisar pago', fr: 'Vérifier le paiement', de: 'Zahlung prüfen', pt: 'Verificar pagamento' }[lang],
  paymentDue: { ja: 'お支払い未完了', en: 'PAYMENT DUE', ko: '결제 미완료', zh: '付款未完成', es: 'PAGO PENDIENTE', fr: 'PAIEMENT EN ATTENTE', de: 'ZAHLUNG OFFEN', pt: 'PAGAMENTO PENDENTE' }[lang],
});

interface Props {
  /** 'landing' matches HomePage header, 'editor' matches EditorPage toolbar */
  variant?: 'landing' | 'editor';
}

export const HeaderAuthButton: React.FC<Props> = ({
  variant = 'landing',
}) => {
  const { lang } = useLanguage();
  const labels = t(lang);
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const openAuthModal = useUIModalStore((s) => s.openAuthModal);
  const openPricingModal = useUIModalStore((s) => s.openPricingModal);
  const plan = useSubscriptionStore((s) => s.plan);
  const subscription = useSubscriptionStore((s) => s.subscription);
  const createPortalSession = useSubscriptionStore((s) => s.createPortalSession);

  const [menuOpen, setMenuOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const paymentNeedsAttention = !!user && subscription?.user_id === user.id && needsBillingRecovery(subscription);

  const openBilling = async () => {
    if (portalLoading) return;
    setPortalError(false);
    setPortalLoading(true);
    trackEvent(paymentNeedsAttention ? 'billing_recovery_click' : 'manage_billing_click', { source: variant });
    try {
      const url = await createPortalSession();
      if (!url) throw new Error('Billing unavailable');
      window.location.href = url;
    } catch {
      setMenuOpen(true);
      setPortalError(true);
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  // Persistent Pro CTA: visible for logged-out visitors and free users in
  // every header this component renders in (landing nav, library nav,
  // editor toolbar). Compact pill so it doesn't disturb those layouts.
  const proCta = plan === 'free' && (
    <button
      type="button"
      className="header-auth-pro-cta"
      onClick={() => paymentNeedsAttention ? openBilling() : openPricingModal('header_cta')}
      disabled={paymentNeedsAttention && portalLoading}
    >
      {paymentNeedsAttention ? labels.reviewPayment : labels.goPro}
    </button>
  );

  if (!user) {
    return (
      <>
        {proCta}
        <button
          type="button"
          className={`header-auth-btn header-auth-btn--${variant}`}
          onClick={() => openAuthModal()}
        >
          {labels.login}
        </button>
      </>
    );
  }

  const label = user.email?.split('@')[0] || 'User';
  return (
    <>
      {proCta}
      <div className="header-auth-user" ref={menuRef}>
      <button
        type="button"
        className={`header-auth-btn header-auth-btn--${variant} is-signed-in`}
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <span className="header-auth-avatar">{label[0]?.toUpperCase()}</span>
        <span className="header-auth-label">{label}</span>
      </button>
      {menuOpen && (
        <div className="header-auth-menu" role="menu">
          <div className="header-auth-menu-email" title={user.email}>
            {user.email}
          </div>
          <div className="header-auth-menu-plan">
            <span className={`header-auth-plan-badge is-${plan}`}>
              {paymentNeedsAttention ? labels.paymentDue : plan === 'pro' ? 'PRO' : 'FREE'}
            </span>
            {plan === 'pro' && subscription?.current_period_end && (
              <span className="header-auth-plan-renewal">
                {/* One-time passes and cancel-at-period-end subs expire; only
                    a live recurring sub actually renews. */}
                {subscription.cancel_at_period_end ||
                subscription.stripe_subscription_id?.startsWith('pass_')
                  ? labels.endsOn
                  : labels.renewsOn}{' '}
                {new Date(subscription.current_period_end).toLocaleDateString(lang)}
              </span>
            )}
          </div>
          {plan === 'pro' || paymentNeedsAttention ? (
            <button
              type="button"
              role="menuitem"
              className="header-auth-menu-item"
              disabled={portalLoading}
              onClick={openBilling}
            >
              {paymentNeedsAttention
                ? `${labels.reviewPayment}${portalLoading ? '…' : ''}`
                : portalLoading ? `${labels.manageBilling}…` : labels.manageBilling}
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="header-auth-menu-item"
              onClick={() => {
                setMenuOpen(false);
                openPricingModal('account_menu');
              }}
            >
              {labels.upgrade}
            </button>
          )}
          {portalError && (
            <div className="header-auth-menu-error">{labels.portalError}</div>
          )}
          <button
            type="button"
            role="menuitem"
            className="header-auth-menu-item"
            onClick={async () => {
              setMenuOpen(false);
              await signOut();
            }}
          >
            {labels.logout}
          </button>
        </div>
      )}
    </div>
    </>
  );
};
